import {Box, Card, CardContent, IconButton, Typography} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {YearlyRevenueData} from '../../../state/types/Dashboard';

const COLORS = [
    '#006a7c', '#ff9900', '#4caf50', '#e91e63', '#9c27b0',
    '#00bcd4', '#ff5722', '#607d8b', '#795548', '#3f51b5',
    '#cddc39', '#f44336', '#009688', '#673ab7', '#8bc34a',
];

const formatAxisValue = (euros: number): string => {
    if (euros >= 1000) {
        return `${(euros / 1000).toFixed(0)}k €`;
    }
    return `${euros.toFixed(0)} €`;
};

const formatTooltipValue = (euros: number): string => {
    return euros.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
};

const MONTH_LABELS: Record<string, string> = {
    '01': 'Jan', '02': 'Feb', '03': 'Mär', '04': 'Apr',
    '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Aug',
    '09': 'Sep', 10: 'Okt', 11: 'Nov', 12: 'Dez',
};

interface YearlyRevenueChartProps {
    data: YearlyRevenueData | null;
    onChangeYear: (year: number) => void;
}

const YearlyRevenueChart = (props: YearlyRevenueChartProps) => {
    const currentYear = new Date().getFullYear();

    if (!props.data) {
        return (
            <Card>
                <CardContent>
                    <Typography variant={'h6'}>Jahresübersicht Umsatz</Typography>
                    <Typography color={'text.secondary'}>Keine Daten vorhanden</Typography>
                </CardContent>
            </Card>
        );
    }

    const allProjectNames = new Set<string>();
    props.data.months.forEach(m => {
        m.projects.forEach(p => allProjectNames.add(p.name));
    });
    const projectNames = Array.from(allProjectNames);

    const chartData = props.data.months.map(m => {
        const monthNum = m.month.split('-')[1];
        const row: Record<string, any> = { month: MONTH_LABELS[monthNum] || monthNum };
        m.projects.forEach(p => {
            row[p.name] = p.revenue / 100;
        });
        return row;
    });

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <Typography variant={'h6'} sx={{ flexGrow: 1 }}>
                        Jahresübersicht Umsatz
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            size={'small'}
                            onClick={() => props.onChangeYear(props.data!.year - 1)}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <Typography variant={'h6'} sx={{ minWidth: 50, textAlign: 'center' }}>
                            {props.data.year}
                        </Typography>
                        <IconButton
                            size={'small'}
                            onClick={() => props.onChangeYear(props.data!.year + 1)}
                            disabled={props.data.year >= currentYear}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </Box>
                </Box>
                <ResponsiveContainer width={'100%'} height={400}>
                    <BarChart data={chartData} margin={{ left: 10 }}>
                        <CartesianGrid strokeDasharray={'3 3'} />
                        <XAxis dataKey={'month'} />
                        <YAxis
                            tickFormatter={formatAxisValue}
                            width={70}
                        />
                        <Tooltip
                            formatter={(value: number) => formatTooltipValue(value)}
                        />
                        <Legend />
                        {projectNames.map((name, index) => (
                            <Bar
                                key={name}
                                dataKey={name}
                                stackId={'revenue'}
                                fill={COLORS[index % COLORS.length]}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};

export default YearlyRevenueChart;
